import { CheckIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/shared/utils/time';

import { ACCESS_COPY } from '../accessCopy';
import {
  useMyAccessRequest,
  useRequestAccess,
} from '../hooks/useAccessRequests';

/** The button's own height at each size, so a held place is the button's shape. */
const PLACE_HEIGHT = { default: 'h-8', sm: 'h-7', lg: 'h-9' } as const;

/**
 * Request access to a workspace (spec 190): the no-access page's one action, the
 * view-only chip's and the band's.
 *
 * Once asked it says so, and keeps saying so on the next visit — the request is the
 * service's, not this page's — until an owner answers. A dismissal is never told
 * (decision Q17): the request is simply gone, and the button comes back.
 *
 * `compact` is the band's form (spec 196 US1): "Request sent" and a check, with the
 * whole sentence on hover, because the band has a line, not a paragraph.
 * `holdPlace` keeps a placeholder of the button's size while the service says
 * whether a request was sent, so the page does not shift when the answer lands
 * (spec 196 US2).
 */
export function RequestAccess({
  workspaceId,
  label = ACCESS_COPY.requestAccess,
  size = 'default',
  compact = false,
  holdPlace = false,
  className,
}: {
  workspaceId: string;
  label?: string;
  size?: 'default' | 'sm' | 'lg';
  compact?: boolean;
  holdPlace?: boolean;
  className?: string;
}) {
  const mine = useMyAccessRequest(workspaceId);
  const ask = useRequestAccess(workspaceId);

  if (mine.isPending) {
    return holdPlace ? (
      <Skeleton
        aria-hidden="true"
        className={cn('w-36 rounded-lg', PLACE_HEIGHT[size], className)}
      />
    ) : null;
  }
  if (mine.requestedAt !== null || ask.isSuccess) {
    // Just sent, it says what happens next; on a later visit, when it was sent.
    const sentence =
      ask.isSuccess || mine.requestedAt === null
        ? ACCESS_COPY.requestSentNow
        : ACCESS_COPY.requestSent(formatRelativeTime(mine.requestedAt));
    if (compact) {
      return (
        <span
          role="status"
          title={sentence}
          className={cn(
            'text-foreground inline-flex items-center gap-1.5 text-xs whitespace-nowrap',
            className,
          )}
        >
          <CheckIcon
            aria-hidden="true"
            strokeWidth={1.5}
            className="text-board-state-done size-3.5"
          />
          <span>{ACCESS_COPY.requestSentShort}</span>
          <span className="sr-only">. {sentence}</span>
        </span>
      );
    }
    return (
      <p
        role="status"
        className={cn(
          'text-muted-foreground flex items-start gap-1.5 text-sm',
          className,
        )}
      >
        <CheckIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className="text-board-state-done mt-0.5 size-4 shrink-0"
        />
        {sentence}
      </p>
    );
  }
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5',
        compact ? 'items-start' : 'items-center',
        className,
      )}
    >
      <Button
        size={size}
        disabled={ask.isPending}
        onClick={() => {
          ask.mutate();
        }}
      >
        {label}
      </Button>
      {ask.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {ask.error.message}
        </p>
      ) : null}
    </div>
  );
}
